import axios, {AxiosError, AxiosResponse} from 'axios'
import {API_URL} from '../../../../../_metronic/helpers'
import {PERMISSION_MISSING_ERROR_CODES, REAUTHENTICATION_ERROR_CODES} from './enums'
import {
  ContractFilters,
  InvoiceFilters,
  InvoiceKind,
  InvoiceReactionKind,
  MemoryUniqueIdDisableResult,
  MemoryUniqueIdResult,
  TaxFileStatusResult,
  TaxWorkspaceContract,
  TaxWorkspaceContractActionResult,
  TaxWorkspaceExcelExecutionResult,
  TaxWorkspaceExcelPreview,
  TaxWorkspaceApiError,
  TaxWorkspaceBulkContractOperation,
  TaxWorkspaceBulkInvoiceOperation,
  TaxWorkspaceBulkResult,
  TaxWorkspaceEnvelope,
  TaxWorkspaceFieldError,
  TaxWorkspaceInvoice,
  TaxWorkspaceInvoiceActionContext,
  TaxWorkspaceInvoiceActionResult,
  TaxWorkspaceInvoiceContractChoices,
  TaxWorkspaceInvoiceSearchResult,
  TaxWorkspaceOwnInvoiceLink,
  TaxWorkspaceLogFilters,
  TaxWorkspaceLogPage,
  TaxWorkspaceProfile,
} from './models'
import {persianDateToTehranEpoch, persianDayToTehranRange} from './tehranDate'

const WORKSPACE_URL = `${API_URL}/tax-workspace`
const WORKSPACE_OPERATION_TIMEOUT_MS = 195000

export type WorkspaceRequestError = Error & {
  code?: string
  correlationId?: string
  fieldMessages?: string[]
  needsDelegation?: boolean
  missingPermission?: boolean
}

const envelopeData = <T>(envelope: TaxWorkspaceEnvelope<T>) => {
  if ((envelope?.errors?.length || 0) > 0 || (envelope?.fieldErrors?.length || 0) > 0) {
    const fieldMessages = (envelope.fieldErrors || []).map(
      (item) => `${item.field ? `${item.field}: ` : ''}${item.errorMessage || 'مقدار نامعتبر است.'}`
    )
    const firstError = envelope.errors?.[0]
    const result = new Error(
      fieldMessages[0] || firstError?.message || 'پاسخ کارپوشه مالیاتی ناموفق بود.'
    ) as WorkspaceRequestError
    result.code = firstError?.code
    result.correlationId = envelope.correlationId
    result.fieldMessages = fieldMessages
    result.needsDelegation = Boolean(
      (result.code && REAUTHENTICATION_ERROR_CODES.has(result.code)) ||
        envelope.errors?.some((item) => item.code && REAUTHENTICATION_ERROR_CODES.has(item.code))
    )
    result.missingPermission = Boolean(
      envelope.errors?.some((item) => item.code && PERMISSION_MISSING_ERROR_CODES.has(item.code))
    )
    throw result
  }
  return envelope.data
}

export const readWorkspaceError = (error: unknown, fallback: string): WorkspaceRequestError => {
  if (error instanceof Error && ('fieldMessages' in error || 'needsDelegation' in error)) {
    return error as WorkspaceRequestError
  }
  const axiosError = error as AxiosError<
    TaxWorkspaceEnvelope<unknown> & {message?: string; code?: string}
  >
  const response = axiosError.response?.data
  const apiErrors: TaxWorkspaceApiError[] = Array.isArray(response?.errors)
    ? response?.errors || []
    : []
  const fieldErrors: TaxWorkspaceFieldError[] = Array.isArray(response?.fieldErrors)
    ? response?.fieldErrors || []
    : []
  const code = apiErrors[0]?.code || response?.code
  const message =
    fieldErrors[0]?.errorMessage ||
    apiErrors[0]?.message ||
    response?.message ||
    (axiosError.code === 'ECONNABORTED'
      ? 'زمان انتظار پاسخ به پایان رسید. دوباره تلاش کنید.'
      : !axiosError.response
      ? 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.'
      : fallback)
  const result = new Error(message) as WorkspaceRequestError
  result.code = code
  result.correlationId =
    response?.correlationId || axiosError.response?.headers?.['x-correlation-id']
  result.fieldMessages = fieldErrors.map(
    (item) => `${item.field ? `${item.field}: ` : ''}${item.errorMessage || 'مقدار نامعتبر است.'}`
  )
  result.needsDelegation = Boolean(
    (code && REAUTHENTICATION_ERROR_CODES.has(code)) ||
      apiErrors.some((item) => item.code && REAUTHENTICATION_ERROR_CODES.has(item.code))
  )
  result.missingPermission = Boolean(
    (code && PERMISSION_MISSING_ERROR_CODES.has(code)) ||
      apiErrors.some((item) => item.code && PERMISSION_MISSING_ERROR_CODES.has(item.code))
  )
  return result
}

const readBlobText = (blob: Blob) => {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}

export const readWorkspaceBlobError = async (
  error: unknown,
  fallback: string
): Promise<WorkspaceRequestError> => {
  const axiosError = error as AxiosError<Blob>
  const blob = axiosError.response?.data
  if (!(blob instanceof Blob)) return readWorkspaceError(error, fallback)
  try {
    const parsed = JSON.parse(await readBlobText(blob))
    return readWorkspaceError(
      {
        ...axiosError,
        response: {...axiosError.response, data: parsed},
      } as AxiosError<TaxWorkspaceEnvelope<unknown>>,
      fallback
    )
  } catch {
    return readWorkspaceError(error, fallback)
  }
}

/**
 * `companyId` names the business a settings screen works on (business settings are keyed by
 * the route, not by the session); without it the backend uses the session's selected business.
 */
const companyScope = (companyId?: number) => (companyId && companyId > 0 ? {companyId} : {})

export const getTaxWorkspaceProfile = async (companyId?: number) => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceProfile>>(
    `${WORKSPACE_URL}/profile`,
    {params: companyScope(companyId), timeout: 30000}
  )
  return envelopeData(response.data)
}

export const requestDelegationOtp = async (
  requestedPermissions: string[],
  validityDurationDays: number,
  companyId?: number
) => {
  const response = await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceProfile>>(
    `${WORKSPACE_URL}/delegation/otp`,
    {...companyScope(companyId), requestedPermissions, validityDurationDays},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const verifyDelegationOtp = async (otp: string, companyId?: number) => {
  const response = await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceProfile>>(
    `${WORKSPACE_URL}/delegation/verify`,
    {...companyScope(companyId), otp},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const getTaxFileStatus = async (query?: {
  economicCode?: string
  nationalId?: string
  followCode?: string
  companyId?: number
}) => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxFileStatusResult>>(
    `${WORKSPACE_URL}/tax-file-status`,
    {
      params: {
        ...companyScope(query?.companyId),
        economicCode: query?.economicCode || undefined,
        nationalId: query?.nationalId || undefined,
        followCode: query?.followCode || undefined,
      },
      timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
    }
  )
  return envelopeData(response.data)
}

export const addMemoryUniqueId = async (
  title?: string,
  branchIds: string[] = [],
  companyId?: number
) => {
  const response = await axios.post<TaxWorkspaceEnvelope<MemoryUniqueIdResult>>(
    `${WORKSPACE_URL}/memory-unique-id`,
    {
      ...companyScope(companyId),
      title: title?.trim() || undefined,
      branchIds: branchIds.length ? branchIds : undefined,
    },
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const disableMemoryUniqueId = async (
  id: string,
  companyTokenId?: number,
  companyId?: number
) => {
  const response = await axios.put<TaxWorkspaceEnvelope<MemoryUniqueIdDisableResult>>(
    `${WORKSPACE_URL}/memory-unique-id/disable`,
    {...companyScope(companyId), id, companyTokenId},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const getTaxWorkspaceLogs = async (filters: TaxWorkspaceLogFilters) => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceLogPage>>(
    `${WORKSPACE_URL}/logs`,
    {
      params: {
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        operation: filters.operation || undefined,
        result: filters.result || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        stage: filters.stage || undefined,
        correlationId: filters.correlationId || undefined,
      },
      timeout: 60000,
    }
  )
  return envelopeData(response.data)
}

export const invoiceParams = (kind: InvoiceKind, filters: InvoiceFilters) => {
  const fromRange = persianDayToTehranRange(filters.issuanceDateFrom)
  const toRange = persianDayToTehranRange(filters.issuanceDateTo)
  return {
    period: filters.period,
    issuanceDateFrom: fromRange.issuanceDateFrom,
    issuanceDateTo: toRange.issuanceDateTo,
    status: filters.status || undefined,
    pattern: filters.pattern || undefined,
    contractBrokerageNumber: filters.contractBrokerageNumber || undefined,
    sellerRole: kind === 'sales' ? filters.sellerRole || undefined : undefined,
    buyerRole: kind === 'purchase' ? filters.buyerRole || undefined : undefined,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
  }
}

const exportParams = <T extends {pageNumber: number; pageSize: number}>(params: T) => ({
  ...params,
  pageNumber: undefined,
  pageSize: undefined,
})

/**
 * One page of a workspace invoice search, with the notes the backend attaches when it had
 * to adjust the search (e.g. the organisation refusing 00:00 of a period's first day).
 */
export const searchTaxWorkspaceInvoices = async (
  kind: InvoiceKind,
  filters: InvoiceFilters
): Promise<TaxWorkspaceInvoiceSearchResult> => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceInvoice[]>>(
    `${WORKSPACE_URL}/invoices/${kind}`,
    {params: invoiceParams(kind, filters), timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  const items = envelopeData(response.data) || []
  return {
    items,
    notices: (response.data?.notices || []).filter((notice) => Boolean(notice && notice.trim())),
    correlationId: response.data?.correlationId,
  }
}

export const getTaxWorkspaceInvoices = async (kind: InvoiceKind, filters: InvoiceFilters) =>
  (await searchTaxWorkspaceInvoices(kind, filters)).items

/**
 * The day's virtual invoices in which this business is the آمر, read by the server from its own
 * sales or purchase list (all pages, up to its limit), so there is no paging here.
 */
export const searchTaxWorkspaceVirtualAsBrokerClient = async (
  baseKind: 'sales' | 'purchase',
  filters: InvoiceFilters
): Promise<TaxWorkspaceInvoiceSearchResult> => {
  const {sellerRole, buyerRole, pageNumber, pageSize, ...params} = invoiceParams(baseKind, filters)
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceInvoice[]>>(
    `${WORKSPACE_URL}/invoices/${baseKind}/virtual-as-broker-client`,
    {params, timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  const items = envelopeData(response.data) || []
  return {
    items,
    notices: (response.data?.notices || []).filter((notice) => Boolean(notice && notice.trim())),
    correlationId: response.data?.correlationId,
  }
}

export const reactToTaxWorkspaceInvoices = async (
  kind: InvoiceReactionKind,
  action: 'approve' | 'reject',
  invoiceTaxIds: string[],
  reasons: string[] = [],
  description = '',
  contexts: TaxWorkspaceInvoiceActionContext[] = []
) => {
  const contextPayload = contexts.length ? {contexts} : {}
  const payload =
    action === 'approve'
      ? {invoiceTaxIds, ...contextPayload}
      : {
          invoiceTaxIds,
          reasons,
          description: description.trim() || undefined,
          ...contextPayload,
        }
  const response = await axios.put<TaxWorkspaceEnvelope<TaxWorkspaceInvoiceActionResult>>(
    `${WORKSPACE_URL}/invoices/${kind}/${action}`,
    payload,
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const bulkReactToTaxWorkspaceInvoices = async (
  kind: InvoiceReactionKind,
  operations: TaxWorkspaceBulkInvoiceOperation[]
) => {
  const response = await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceBulkResult>>(
    `${WORKSPACE_URL}/bulk/invoices`,
    {kind, operations},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

const optionalDate = (value: string, endOfDay = false) =>
  value ? persianDateToTehranEpoch(value, endOfDay) : undefined

export const contractParams = (filters: ContractFilters) => ({
  status: filters.status || undefined,
  contractBrokerageId: filters.contractBrokerageId || undefined,
  contractBrokerageNumber: filters.contractBrokerageNumber || undefined,
  contractBrokerageInternalNumber: filters.contractBrokerageInternalNumber || undefined,
  brokerClientEconomicNumber: filters.brokerClientEconomicNumber || undefined,
  brokerEconomicNumber: filters.brokerEconomicNumber || undefined,
  issueDateFrom: optionalDate(filters.issueDateFrom),
  issueDateTo: optionalDate(filters.issueDateTo, true),
  createDateFrom: optionalDate(filters.createDateFrom),
  createDateTo: optionalDate(filters.createDateTo, true),
  pageNumber: filters.pageNumber,
  pageSize: filters.pageSize,
})

export const getTaxWorkspaceContracts = async (filters: ContractFilters) => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceContract[]>>(
    `${WORKSPACE_URL}/brokerage-contracts`,
    {
      params: contractParams(filters),
      timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
    }
  )
  return envelopeData(response.data) || []
}

/**
 * Which of these tax ids are invoices this system issued, for a business the user may see. The
 * answer is a local read; the organisation is not called. Repeated `taxIds=` keys, as Web API
 * binds a list from the query string.
 */
export const getTaxWorkspaceOwnInvoiceLinks = async (taxIds: string[], signal?: AbortSignal) => {
  const query = taxIds.map((taxId) => `taxIds=${encodeURIComponent(taxId)}`).join('&')
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceOwnInvoiceLink[]>>(
    `${WORKSPACE_URL}/own-invoice-links?${query}`,
    {signal}
  )
  return envelopeData(response.data) || []
}

/**
 * The approved contracts the invoice item form offers for «شماره قرارداد معامله از طریق شخص
 * ثالث». A business without a usable workspace gets `available: false`, never an error.
 */
export const getTaxWorkspaceInvoiceContractChoices = async (
  request: {pattern?: number; production?: boolean; refresh?: boolean},
  signal?: AbortSignal
) => {
  const response = await axios.get<TaxWorkspaceEnvelope<TaxWorkspaceInvoiceContractChoices>>(
    `${WORKSPACE_URL}/brokerage-contracts/invoice-choices`,
    {
      params: {
        pattern: request.pattern || undefined,
        production: request.production,
        refresh: request.refresh || undefined,
      },
      timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
      signal,
    }
  )
  return envelopeData(response.data)
}

export const reactToTaxWorkspaceContract = async (
  action: 'approve' | 'reject',
  contractBrokerageId: string
) => {
  const response = await axios.put<TaxWorkspaceEnvelope<TaxWorkspaceContractActionResult>>(
    `${WORKSPACE_URL}/brokerage-contracts/${action}`,
    {contractBrokerageId},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

export const bulkReactToTaxWorkspaceContracts = async (
  operations: TaxWorkspaceBulkContractOperation[]
) => {
  const response = await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceBulkResult>>(
    `${WORKSPACE_URL}/bulk/contracts`,
    {operations},
    {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )
  return envelopeData(response.data)
}

const fileForm = (file: File) => {
  const form = new FormData()
  form.append('file', file, file.name)
  return form
}

const excelEnvelope = <T>(response: AxiosResponse<TaxWorkspaceEnvelope<T>>) =>
  envelopeData(response.data)

export const exportTaxWorkspaceInvoices = (kind: InvoiceKind, filters: InvoiceFilters) =>
  axios.get<Blob>(`${WORKSPACE_URL}/excel/invoices/${kind}/export`, {
    params: exportParams(invoiceParams(kind, filters)),
    responseType: 'blob',
    timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
  })

export const getTaxWorkspaceInvoiceTemplate = (kind: InvoiceKind) =>
  axios.get<Blob>(`${WORKSPACE_URL}/excel/invoices/${kind}/template`, {
    responseType: 'blob',
    timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
  })

export const previewTaxWorkspaceInvoiceExcel = async (kind: InvoiceKind, file: File) =>
  excelEnvelope(
    await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceExcelPreview>>(
      `${WORKSPACE_URL}/excel/invoices/${kind}/preview`,
      fileForm(file),
      {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
    )
  )

export const executeTaxWorkspaceInvoiceExcel = async (kind: InvoiceKind, previewToken: string) =>
  excelEnvelope(
    await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceExcelExecutionResult>>(
      `${WORKSPACE_URL}/excel/invoices/${kind}/execute`,
      {previewToken},
      {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
    )
  )

export const exportTaxWorkspaceContracts = (filters: ContractFilters) =>
  axios.get<Blob>(`${WORKSPACE_URL}/excel/contracts/export`, {
    params: exportParams(contractParams(filters)),
    responseType: 'blob',
    timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
  })

export const getTaxWorkspaceContractTemplate = () =>
  axios.get<Blob>(`${WORKSPACE_URL}/excel/contracts/template`, {
    responseType: 'blob',
    timeout: WORKSPACE_OPERATION_TIMEOUT_MS,
  })

export const previewTaxWorkspaceContractExcel = async (file: File) =>
  excelEnvelope(
    await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceExcelPreview>>(
      `${WORKSPACE_URL}/excel/contracts/preview`,
      fileForm(file),
      {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
    )
  )

export const executeTaxWorkspaceContractExcel = async (previewToken: string) =>
  excelEnvelope(
    await axios.post<TaxWorkspaceEnvelope<TaxWorkspaceExcelExecutionResult>>(
      `${WORKSPACE_URL}/excel/contracts/execute`,
      {previewToken},
      {timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
    )
  )

export const getTaxWorkspaceExcelResult = (resultToken: string) =>
  axios.post<Blob>(
    `${WORKSPACE_URL}/excel/result`,
    {resultToken},
    {responseType: 'blob', timeout: WORKSPACE_OPERATION_TIMEOUT_MS}
  )

const safeDownloadFileName = (value: string, fallback: string) => {
  const withoutControlCharacters = Array.from(value, (character) =>
    character.charCodeAt(0) < 32 ? '_' : character
  ).join('')
  const cleaned = withoutControlCharacters
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/^\.+/, '')
    .trim()
    .slice(0, 180)
  return cleaned || fallback
}

export const downloadWorkspaceBlob = (response: AxiosResponse<Blob>, fallbackFileName: string) => {
  const disposition = response.headers?.['content-disposition'] as string | undefined
  const utf8 = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const ascii = disposition?.match(/filename="?([^";]+)"?/i)?.[1]
  let headerName = utf8 || ascii || ''
  try {
    headerName = decodeURIComponent(headerName)
  } catch {
    // Keep the server-provided ASCII fallback when RFC 5987 decoding is not possible.
  }
  const fileName = safeDownloadFileName(headerName, fallbackFileName)
  const href = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(href), 1_000)
}
